import type { Scope, ScopeLimits } from "./reportTypes"

// Fallback for older bootstrap responses. Current Sofia returns this policy.
const DEFAULT_LIMITS: ScopeLimits = {
  request_budget: 209,
  catalog_reads: 9,
  simulation_volume_reads: 20,
  simulation_reads_per_agent: 16,
  automatic_simulation_agents: 5,
  upload_reads_per_value: 36,
}

export function effectiveReportScope(scope: Scope): Scope {
  const sourceScope =
    scope.source === "monitoring"
      ? { ...scope, agent_ids: [], test_set_ids: [], top_n: null }
      : scope
  if (
    sourceScope.upload_agent &&
    (sourceScope.upload_agent.metadata_key ||
      sourceScope.upload_agent.values.length)
  ) {
    return sourceScope
  }
  const { upload_agent: _emptyUploadFilter, ...unfilteredScope } = sourceScope
  return unfilteredScope
}

export function reportScopeCapacity(
  scope: Scope,
  limits: ScopeLimits = DEFAULT_LIMITS,
) {
  const effectiveScope = effectiveReportScope(scope)
  const simulations = effectiveScope.source !== "monitoring"
  const uploads = effectiveScope.source !== "simulations"
  const agentCount =
    new Set(effectiveScope.agent_ids).size ||
    Math.min(
      effectiveScope.top_n || limits.automatic_simulation_agents,
      limits.automatic_simulation_agents,
    )
  const simulationReads = simulations
    ? limits.simulation_volume_reads +
      agentCount * limits.simulation_reads_per_agent
    : 0
  // Mirrors sofia_agent scope_limits.required_scope_reads: an upload_agent
  // selection charges identities = len(values) or 1 (a field-only selection is
  // one identity); without an upload_agent filter the server charges a second
  // full volume-plus-per-agent scan instead.
  const uploadIdentities = effectiveScope.upload_agent
    ? new Set(effectiveScope.upload_agent.values).size || 1
    : 0
  const uploadReads = uploads
    ? uploadIdentities
      ? uploadIdentities * limits.upload_reads_per_value
      : limits.simulation_volume_reads +
        agentCount * limits.simulation_reads_per_agent
    : 0
  const required = limits.catalog_reads + simulationReads + uploadReads
  // Without an upload_agent the upload scan adds a second per-agent charge, so
  // the budget consumed per selected agent doubles and the agent cap must be
  // solved against the doubled slope instead of a single per-agent rate.
  const perAgentReads =
    (simulations ? limits.simulation_reads_per_agent : 0) +
    (uploads && !effectiveScope.upload_agent
      ? limits.simulation_reads_per_agent
      : 0)
  const fixedReads = required - agentCount * perAgentReads
  return {
    maxSimulationAgents: Math.max(
      0,
      Math.floor(
        (limits.request_budget - fixedReads) /
          (perAgentReads || limits.simulation_reads_per_agent),
      ),
    ),
    maxUploadValues: Math.max(
      0,
      Math.floor(
        (limits.request_budget - limits.catalog_reads - simulationReads) /
          limits.upload_reads_per_value,
      ),
    ),
    error:
      required > limits.request_budget
        ? "This combination exceeds the supported notebook scope. Select fewer simulation agents or uploaded metadata values, or choose one data source."
        : null,
  }
}
